import type { RoleName, Permission } from './types';

// Constants for role hierarchy (higher number = more permissions)
export const ROLE_HIERARCHY: Record<RoleName, number> = {
    user: 1,
    editor: 2,
    admin: 3,
} as const;

// Default permissions for each role
export const DEFAULT_ROLE_PERMISSIONS: Record<RoleName, Permission[]> = {
    admin: [
        // User permissions
        { resource: 'user', action: 'create' },
        { resource: 'user', action: 'list' },
        { resource: 'user', action: 'set-role' },
        { resource: 'user', action: 'ban' },
        { resource: 'user', action: 'impersonate' },
        { resource: 'user', action: 'delete' },
        { resource: 'user', action: 'set-password' },
        // Project permissions
        { resource: 'project', action: 'create' },
        { resource: 'project', action: 'view' },
        { resource: 'project', action: 'update' },
        { resource: 'project', action: 'delete' },
        // Purchase Order permissions
        { resource: 'purchase-order', action: 'create' },
        { resource: 'purchase-order', action: 'view' },
        { resource: 'purchase-order', action: 'update' },
        { resource: 'purchase-order', action: 'delete' },
        { resource: 'purchase-order', action: 'approve' },
        { resource: 'purchase-order', action: 'cancel' },
        { resource: 'purchase-order', action: 'fulfill' },
        { resource: 'purchase-order', action: 'export' },
        // Sales Order permissions
        { resource: 'sales-order', action: 'create' },
        { resource: 'sales-order', action: 'view' },
        { resource: 'sales-order', action: 'update' },
        { resource: 'sales-order', action: 'delete' },
        { resource: 'sales-order', action: 'approve' },
        { resource: 'sales-order', action: 'cancel' },
        { resource: 'sales-order', action: 'fulfill' },
        { resource: 'sales-order', action: 'invoice' },
        { resource: 'sales-order', action: 'export' },
        // Product permissions
        { resource: 'product', action: 'create' },
        { resource: 'product', action: 'view' },
        { resource: 'product', action: 'update' },
        { resource: 'product', action: 'delete' },
        // Deals permissions
        { resource: 'deals', action: 'create' },
        { resource: 'deals', action: 'view' },
        { resource: 'deals', action: 'update' },
        { resource: 'deals', action: 'delete' },
        { resource: 'deals', action: 'approve' },
        { resource: 'deals', action: 'cancel' },
        // Live permissions
        { resource: 'live', action: 'create' },
        { resource: 'live', action: 'view' },
        { resource: 'live', action: 'update' },
        { resource: 'live', action: 'delete' },
        // Vendor permissions
        { resource: 'vendor', action: 'create' },
        { resource: 'vendor', action: 'view' },
        { resource: 'vendor', action: 'update' },
        { resource: 'vendor', action: 'delete' },
        // Customer permissions
        { resource: 'customer', action: 'create' },
        { resource: 'customer', action: 'view' },
        { resource: 'customer', action: 'update' },
        { resource: 'customer', action: 'delete' },
    ],
    editor: [
        // Project permissions
        { resource: 'project', action: 'create' },
        { resource: 'project', action: 'view' },
        { resource: 'project', action: 'update' },
        // Purchase Order permissions
        { resource: 'purchase-order', action: 'create' },
        { resource: 'purchase-order', action: 'view' },
        { resource: 'purchase-order', action: 'update' },
        { resource: 'purchase-order', action: 'fulfill' },
        { resource: 'purchase-order', action: 'export' },
        // Sales Order permissions
        { resource: 'sales-order', action: 'create' },
        { resource: 'sales-order', action: 'view' },
        { resource: 'sales-order', action: 'update' },
        { resource: 'sales-order', action: 'fulfill' },
        { resource: 'sales-order', action: 'invoice' },
        { resource: 'sales-order', action: 'export' },
        // Product permissions
        { resource: 'product', action: 'create' },
        { resource: 'product', action: 'view' },
        { resource: 'product', action: 'update' },
        // Deals permissions
        { resource: 'deals', action: 'create' },
        { resource: 'deals', action: 'view' },
        { resource: 'deals', action: 'update' },
        // Live permissions
        { resource: 'live', action: 'view' },
        { resource: 'live', action: 'update' },
        // Vendor permissions
        { resource: 'vendor', action: 'create' },
        { resource: 'vendor', action: 'view' },
        { resource: 'vendor', action: 'update' },
        // Customer permissions
        { resource: 'customer', action: 'create' },
        { resource: 'customer', action: 'view' },
        { resource: 'customer', action: 'update' },
    ],
    user: [
        // Project permissions
        { resource: 'project', action: 'view' },
        // Purchase Order permissions (read-only)
        { resource: 'purchase-order', action: 'view' },
        // Sales Order permissions (read-only)
        { resource: 'sales-order', action: 'view' },
        // Product permissions (read-only)
        { resource: 'product', action: 'view' },
        // Deals permissions (read-only)
        { resource: 'deals', action: 'view' },
        // Live permissions (read-only)
        { resource: 'live', action: 'view' },
        // Vendor permissions (read-only)
        { resource: 'vendor', action: 'view' },
        // Customer permissions (read-only)
        { resource: 'customer', action: 'view' },
    ],
} as const;

// Valid roles array
export const VALID_ROLES: RoleName[] = ['admin', 'editor', 'user'];

// Valid resources array
export const VALID_RESOURCES = [
    'user',
    'project',
    'purchase-order',
    'sales-order',
    'product',
    'deals',
    'live',
    'vendor',
    'customer'
] as const;